import { Medal, Star, Trophy } from 'lucide-react';
import { useMemo } from 'react';
import InfiniteSlider from './smoothui/infinite-slider';
import type { PublicMedalist } from '../services/publicHighlights';
import { formatClassName } from '../domain/classCode';

interface PublicMedalistSliderProps {
  medalists: PublicMedalist[];
  loading: boolean;
}

export function PublicMedalistSlider({ medalists, loading }: PublicMedalistSliderProps) {
  const rails = useMemo(() => buildRails(medalists), [medalists]);

  if (loading) {
    return <p className="public-medalists-status" role="status">Carregando destaques...</p>;
  }

  if (!medalists.length) {
    return (
      <div className="public-medalists-empty" role="status">
        <span aria-hidden="true"><Trophy size={25} /></span>
        <div>
          <strong>O quadro aguarda as conquistas de 2026</strong>
          <p>Os medalhistas aparecerão aqui após a publicação dos resultados.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-medalist-rails" aria-label="Medalhistas de 2026">
      {rails.map((rail) => rail.items.length ? (
        <InfiniteSlider
          className="public-medalists-slider"
          direction="horizontal"
          gap={10}
          speed={rail.speed}
          reverse={rail.reverse}
          pauseOnHover
          key={rail.id}
        >
          {rail.items.map((medalist) => (
            <PublicMedalistCard medalist={medalist} multi={medalist.multi} medalCount={medalist.medalCount} key={medalist.id} />
          ))}
        </InfiniteSlider>
      ) : null)}
    </div>
  );
}

export function PublicMedalistCard({ medalist, multi = false, medalCount }: { medalist: PublicMedalist; multi?: boolean; medalCount?: number }) {
  return (
    <article className={`public-medalist-card ${multi ? 'is-multi' : getAwardTone(medalist.award)}`}>
      <span className="public-medalist-icon" aria-hidden="true">{multi ? <Star size={20} fill="currentColor" /> : <Medal size={21} />}</span>
      <div className="public-medalist-copy">
        <strong>{multi ? 'Multimedalhista' : medalist.award}</strong>
        <h3>{medalist.studentName}</h3>
        <p>{formatClassName(medalist.className)} · {multi ? `${medalCount ?? 0} medalhas` : medalist.olympiadAcronym}</p>
      </div>
    </article>
  );
}

type RailMedalist = PublicMedalist & { multi?: boolean; medalCount?: number };

function buildRails(medalists: PublicMedalist[]): Array<{ id: string; items: RailMedalist[]; reverse: boolean; speed: number }> {
  const byStudent = medalists.reduce((groups, medalist) => {
    const key = `${medalist.studentName.toLocaleLowerCase('pt-BR')}\u0000${medalist.className}`;
    const current = groups.get(key) ?? [];
    current.push(medalist);
    groups.set(key, current);
    return groups;
  }, new Map<string, PublicMedalist[]>());
  const multi = [...byStudent.values()].filter((items) => items.length > 1).map((items) => ({ ...items[0], id: `multi-${items[0].id}`, multi: true, medalCount: items.length }));
  const diamondAndGold: RailMedalist[] = medalists.filter((item) => {
    const award = item.award.toLocaleLowerCase('pt-BR');
    return award.includes('diamante') || award.includes('ouro');
  });
  const silverBronzeAndHonor: RailMedalist[] = medalists.filter((item) => {
    const award = item.award.toLocaleLowerCase('pt-BR');
    return award.includes('prata') || award.includes('bronze') || award.includes('honra ao mérito');
  });

  return [
    { id: 'multi', items: shuffle(multi), reverse: false, speed: 31 },
    { id: 'diamond-gold', items: shuffle(diamondAndGold), reverse: true, speed: 27 },
    { id: 'silver-bronze-honor', items: shuffle(silverBronzeAndHonor), reverse: false, speed: 29 },
  ];
}

function shuffle<T>(items: T[]) {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
}

function getAwardTone(award: string) {
  const normalized = award.toLocaleLowerCase('pt-BR');
  if (normalized.includes('platina')) return 'award-platinum';
  if (normalized.includes('diamante')) return 'award-diamond';
  if (normalized.includes('ouro')) return 'award-gold';
  if (normalized.includes('prata')) return 'award-silver';
  if (normalized.includes('bronze')) return 'award-bronze';
  if (normalized.includes('honra ao mérito')) return 'award-honor';
  return 'award-medal';
}
